import React, { useCallback, useState, useEffect } from 'react'
import merge from 'lodash/merge'
import styled from 'styled-components'
import { Typography, IconButton } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import L from 'leaflet'
import {
  Map as LeafletMap,
  Marker,
  Tooltip as LeafletTooltip,
  Popup,
} from 'react-leaflet'
import { leafletLocale, Button, formatDeviceId } from 'sputnik-ui'

import SputnikIcon from 'assets/Sputnik.svg'
import GateIcon from 'assets/gate.svg'
import GateYellowIcon from 'assets/gate_yellow.svg'
import ISee from 'assets/ISee.svg'
import { Restore } from '@material-ui/icons'
import YandexTileLayer from 'components/atoms/YandexTileLayer'
import useYandexMapsApi from 'hooks/useYandexMapsApi'

L.drawLocal = merge(L.drawLocal, leafletLocale)

const sputnik = L.icon({
  iconUrl: SputnikIcon,
  iconSize: [32, 32],
  iconAnchor: [12, 0],
})

const gate = L.icon({
  iconUrl: GateIcon,
  iconSize: [24, 24],
  iconAnchor: [12, 0],
})

const gateYellow = L.icon({
  iconUrl: GateYellowIcon,
  iconSize: [24, 24],
  iconAnchor: [12, 0],
})

const cameraIcon = L.icon({
  iconUrl: ISee,
  iconSize: [32, 32],
  iconAnchor: [12, 0],
})

const StyledPopup = styled(Popup)`
  .leaflet-popup-content-wrapper {
    box-shadow: ${(p) => p.theme.shadows.main};
    border-radius: ${(p) => p.theme.sizes.borderRadius};
  }

  .leaflet-popup-content {
    margin: 0;
    padding: 0.625rem;

    & p {
      margin: 0;
    }
  }
`

const StyledMap = styled(LeafletMap)`
  width: 100%;
  height: 100%;
  z-index: 0;

  .leaflet-tile-pane {
    filter: grayscale(100%);
  }

  .leaflet-bar {
    box-shadow: ${(p) => p.theme.shadows.main};
    border-radius: 15px;
    overflow: hidden;
    border: none;

    a {
      border-bottom-color: ${(p) => p.theme.palette.other.border};
      color: ${(p) => p.theme.palette.text.primary};
    }
  }
`

const Tooltip = styled(LeafletTooltip)`
  background: ${(p) => p.theme.palette.primary.main};
  color: ${(p) => p.theme.palette.primary.contrastText};
  border: none;
  border-radius: ${(p) => p.theme.sizes.borderRadius};
  font-family: ${(p) => p.theme.fonts.primary};

  &::before {
    border-top-color: ${(p) => p.theme.palette.primary.main};
  }
`

const getIcon = (address_type, device_type, isUpdated) => {
  if (device_type === 'camera') return cameraIcon
  return sputnik
}

const MAX_DISTANCE = 500

const MapSingleMarker = ({
  initialMarker,
  withConfirm = false,
  onUpdate,
  center,
  ...props
}) => {
  const [marker, setMarker] = useState(initialMarker)
  const [error, setError] = useState('')
  const [isUpdated, setIsUpdated] = useState(false)
  const isYandexMapsReady = useYandexMapsApi()

  useEffect(() => {
    setMarker(initialMarker)
  }, [initialMarker])

  const { t } = useTranslation(['common', 'devices'])

  const handleReset = useCallback(() => {
    setMarker(initialMarker)
    setIsUpdated(false)
  }, [initialMarker])

  const onDragEnd = useCallback(
    (e) => {
      const originalMarker = { ...initialMarker, lng: initialMarker.lon }
      const newPos = e.target.getLatLng()
      const distanceToOriginal = newPos.distanceTo(originalMarker)

      if (distanceToOriginal > MAX_DISTANCE) {
        setError(t('devices:map.long_away'))
        setTimeout(() => {
          setError('')
        }, 1500)
      } else {
        const updated = {
          ...marker,
          lat: newPos.lat,
          lon: newPos.lng,
          lng: newPos.lng,
        }
        setMarker(updated)
        setIsUpdated(true)
        if (!withConfirm) {
          onUpdate(updated)
        }
      }
    },
    [initialMarker, marker, onUpdate, withConfirm, t]
  )

  const {
    lat,
    lon,
    address_type,
    address_uuid,
    serial_number,
    short_address,
    device_type,
  } = marker || {}

  return lat ? (
    <>
      <StyledMap
        center={center?.lat ? center : { lat, lon }}
        zoom={17}
        scrollWheelZoom={false}
        touchZoom={false}
        {...props}
      >
        {isYandexMapsReady && <YandexTileLayer type="map" />}
        <Marker
          draggable
          onDragEnd={onDragEnd}
          position={{ lat, lon }}
          icon={getIcon(address_type, device_type, isUpdated)}
          zIndexOffset={address_type === 'gate' ? 2 : 1}
        >
          <StyledPopup minWidth={90}>
            <Typography variant="body1" style={{ marginBottom: '.325rem' }}>
              {device_type === 'camera'
                ? t('devices:units.camera')
                : address_type === 'gate'
                ? t('devices:units.gate')
                : t('devices:units.intercom')}{' '}
              {device_type === 'intercom'
                ? formatDeviceId(serial_number)
                : serial_number}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {' '}
              {short_address}
            </Typography>
            {isUpdated && withConfirm && (
              <Button
                primary
                style={{ marginTop: '.625rem', width: '100%' }}
                onClick={() =>
                  onUpdate({ uuid: address_uuid, lat, lon }).then(() =>
                    setIsUpdated(false)
                  )
                }
                size="small"
              >
                {t('devices:any.save_geo')}
              </Button>
            )}
          </StyledPopup>
          <Tooltip direction="top">
            {device_type === 'camera' ? (
              `${t('devices:units.camera')} ${serial_number}`
            ) : address_type === 'gate' ? (
              `${t('devices:map.drag_gate')}${
                withConfirm ? ` ${t('devices:map.move_save')}` : ''
              }`
            ) : (
              <span>
                {t('devices:units.intercom')} {formatDeviceId(serial_number)}
              </span>
            )}
          </Tooltip>
        </Marker>
      </StyledMap>
      {error && (
        <Typography
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            width: '100%',
            transform: 'translate(0,-50%)',
            zIndex: 1000,
          }}
          align="center"
          variant="h6"
          color="error"
        >
          {error}
        </Typography>
      )}
      {isUpdated && withConfirm && (
        <div
          style={{
            position: 'absolute',
            bottom: '4rem',
            left: '1rem',
            display: 'flex',
          }}
        >
          <IconButton
            secondary
            style={{ marginRight: '.325rem' }}
            onClick={handleReset}
            color="primary"
          >
            <Restore />
          </IconButton>
        </div>
      )}
    </>
  ) : null
}

export default MapSingleMarker
